import { ResponsiveKpis } from '@/components/responsive-kpis';
import { PageActions } from '@/components/page-actions';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useEffect, useMemo, type ReactNode } from 'react';
import { ScrollTable } from '@/components/scroll-table';
import { TabStrip } from '@/components/tab-strip';
import { Link } from '@tanstack/react-router';
import {
  useCampaigns,
  useMarkupByCampaign,
  useMarkupByChannel,
  useMarkupByProduction,
  useMarkupSummary,
  useProductions,
  useReportingQuery,
} from '@/api/hooks';
import type {
  Campaign,
  MarkupBreakdownRow,
  Production,
} from '@/api/types';
import { useCan } from '@/auth/capabilities';
import { DateFilter } from '@/components/dropdowns';
import { QueryErrorNote } from '@/components/error-panel';
import { ChannelPill, fmt, Icons, MoneyCell } from '@/components/primitives';
import { ReportingFreshnessWarnings } from '@/components/reporting-freshness';
import { ReportingDataControls } from '@/components/reporting-controls';
import {
  ScopeSearchPicker,
  renderCampaignRow,
  renderProductionRow,
} from '@/components/scope-search-picker';
import { csvFilename, downloadCsv } from '@/lib/csv';
import { fmtDateInput, nyDateInputToMs } from '@/lib/datetime';
import {
  markupReportRequest,
  operationalFreshnessRequest,
  serializeOperationalReportSearch,
  updateMarkupReportSearch,
} from '@/lib/operational-report-search';
import type {
  MarkupReportSearch,
  MarkupReportSearchPatch,
  MarkupReportTab,
} from '@/lib/operational-report-search';
import {
  reportMetricIsAvailable,
  reportMetricUnavailableReason,
  reportingMetricAvailability,
} from '@/lib/reporting-availability';

interface DateState {
  preset: string;
  start: Date;
  end: Date;
}

interface MarkupReportsProps {
  search: MarkupReportSearch;
  onSearchChange: (
    patch: MarkupReportSearchPatch,
    options?: { replace?: boolean },
  ) => void;
}

interface CampaignMarkup {
  campaign_id: string;
  name: string;
  production_id: string;
  channel: 'meta' | 'ctv' | 'dsp';
  status: string;
  spend_cents: number;
  markup_cents: number;
  marked_up_spend_cents: number;
  marked_up: boolean;
}

interface GroupedMarkup {
  id: string;
  name: string;
  spend_cents: number;
  marked_up_spend_cents: number;
  markup_cents: number;
  marked_up_count: number;
  total_count: number;
}

const MARKUP_TABS: Array<{ id: MarkupReportTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaign', label: 'By Campaign' },
  { id: 'channel', label: 'By Channel' },
  { id: 'production', label: 'By Assigned Production' },
];

const CHANNELS = [
  { id: 'meta', name: 'Meta' },
  { id: 'ctv', name: 'CTV' },
  { id: 'dsp', name: 'Display' },
] as const;

// Reported spend is the client gross, so each rate is a true share of it:
// Meta 15%, CTV (30 − 25)/30 = 16.7%, Display (8 − 6)/8 = 25.0%. Every rate
// shown on this screen divides by gross, so the labels and the measured
// blended rate finally describe the same quantity.
const MARKUP_RATE_LABELS: Record<string, string> = {
  meta: '15% of gross',
  ctv: '$25 → $30 CPM · 16.7% of gross',
  dsp: '$6 → $8 CPM · 25.0% of gross',
};

function n(value: unknown): number {
  return Number(value) || 0;
}

/** Percentage of gross spend retained as agency margin. */
function marginRatePercent(marginCents: number, grossSpendCents: number): number {
  return grossSpendCents ? marginCents / grossSpendCents * 100 : 0;
}

function aggregateCampaignRows(
  rows: MarkupBreakdownRow[],
  campaignMap: ReadonlyMap<string, Campaign>,
): CampaignMarkup[] {
  const grouped = new Map<string, CampaignMarkup>();
  for (const row of rows) {
    const campaign = campaignMap.get(row.campaign_id);
    const current = grouped.get(row.campaign_id) ?? {
      campaign_id: row.campaign_id,
      name: row.name ?? campaign?.name ?? row.campaign_id,
      production_id: row.production_id,
      channel: row.channel,
      status: campaign?.status ?? 'draft',
      spend_cents: 0,
      markup_cents: 0,
      marked_up_spend_cents: 0,
      marked_up: false,
    };
    const spend = n(row.spend_cents);
    current.spend_cents += spend;
    current.markup_cents += n(row.markup_cents);
    current.marked_up = current.marked_up || row.marked_up;
    current.marked_up_spend_cents += row.marked_up
      ? n(row.marked_up_spend_cents ?? spend)
      : 0;
    grouped.set(row.campaign_id, current);
  }
  return Array.from(grouped.values());
}

function aggregateGroupedRows(
  rows: MarkupBreakdownRow[],
  getId: (row: MarkupBreakdownRow) => string,
  getName: (row: MarkupBreakdownRow, id: string) => string,
): GroupedMarkup[] {
  const grouped = new Map<string, GroupedMarkup & { campaigns: Set<string>; markedCampaigns: Set<string> }>();
  for (const row of rows) {
    const id = getId(row);
    if (!id) continue;
    const current = grouped.get(id) ?? {
      id,
      name: getName(row, id),
      spend_cents: 0,
      marked_up_spend_cents: 0,
      markup_cents: 0,
      marked_up_count: 0,
      total_count: 0,
      campaigns: new Set<string>(),
      markedCampaigns: new Set<string>(),
    };
    const spend = n(row.spend_cents);
    current.spend_cents += spend;
    current.markup_cents += n(row.markup_cents);
    current.marked_up_spend_cents += row.marked_up
      ? n(row.marked_up_spend_cents ?? spend)
      : 0;
    current.campaigns.add(row.campaign_id);
    if (row.marked_up) current.markedCampaigns.add(row.campaign_id);
    current.total_count = row.total_count === undefined
      ? current.campaigns.size
      : Math.max(current.total_count, n(row.total_count));
    current.marked_up_count = row.marked_up_count === undefined
      ? current.markedCampaigns.size
      : Math.max(current.marked_up_count, n(row.marked_up_count));
    grouped.set(id, current);
  }
  return Array.from(grouped.values()).map(({ campaigns: _campaigns, markedCampaigns: _markedCampaigns, ...row }) => row);
}

function MarkupReports({ search, onSearchChange }: MarkupReportsProps) {
  const canExport = useCan('reports_export');
  const tab = search.tab;
  const scopeId = search.scopeId ?? null;
  const channel = search.channel;
  const date = useMemo<DateState>(() => ({
    preset: 'custom',
    start: new Date(nyDateInputToMs(search.from)),
    end: new Date(nyDateInputToMs(search.to)),
  }), [search.from, search.to]);

  const campaignsQuery = useCampaigns();
  const productionsQuery = useProductions();
  const campaigns = campaignsQuery.data ?? [];
  const productions = productionsQuery.data ?? [];
  const campaignMap = useMemo(
    () => new Map(campaigns.map((campaign) => [campaign.id as string, campaign])),
    [campaigns],
  );
  const productionMap = useMemo(
    () => new Map(productions.map((production) => [production.id as string, production])),
    [productions],
  );

  useEffect(() => {
    if (tab === 'campaign') {
      const next = scopeId && campaigns.some((row) => row.id === scopeId)
        ? scopeId
        : campaigns[0]?.id;
      if (next !== scopeId) onSearchChange({ scopeId: next }, { replace: true });
    } else if (tab === 'production') {
      const next = scopeId && productions.some((row) => row.id === scopeId)
        ? scopeId
        : productions[0]?.id;
      if (next !== scopeId) onSearchChange({ scopeId: next }, { replace: true });
    } else if (scopeId !== null) {
      onSearchChange({ scopeId: undefined }, { replace: true });
    }
  }, [campaigns, onSearchChange, productions, scopeId, tab]);

  const query = useMemo(() => markupReportRequest(search), [search]);
  const freshnessRequest = useMemo(() => operationalFreshnessRequest(search), [search]);
  const freshnessQuery = useReportingQuery(freshnessRequest);
  const availability = reportingMetricAvailability(
    'delivery',
    freshnessQuery.data?.freshness,
  );
  const deliveryAvailable = reportMetricIsAvailable(availability, 'delivery');
  const deliveryReason = reportMetricUnavailableReason(availability, 'delivery');

  const summaryQuery = useMarkupSummary(query);
  const byCampaignQuery = useMarkupByCampaign(query);
  const byChannelQuery = useMarkupByChannel(query);
  const byProductionQuery = useMarkupByProduction(query);
  const summary = summaryQuery.data;

  const campaignRows = useMemo(
    () => deliveryAvailable
      ? aggregateCampaignRows(byCampaignQuery.data ?? [], campaignMap)
      : [],
    [byCampaignQuery.data, campaignMap, deliveryAvailable],
  );
  const channelRows = useMemo(
    () => deliveryAvailable
      ? aggregateGroupedRows(
          byChannelQuery.data ?? [],
          (row) => row.channel,
          (_row, id) => CHANNELS.find((item) => item.id === id)?.name ?? id,
        ).sort((a, b) => b.markup_cents - a.markup_cents)
      : [],
    [byChannelQuery.data, deliveryAvailable],
  );
  const productionRows = useMemo(
    () => deliveryAvailable
      ? aggregateGroupedRows(
          byProductionQuery.data ?? [],
          (row) => row.production_id,
          (row, id) => row.name ?? productionMap.get(id)?.name ?? id,
        ).sort((a, b) => b.markup_cents - a.markup_cents)
      : [],
    [byProductionQuery.data, deliveryAvailable, productionMap],
  );

  const totalSpend = n(summary?.total_spend_cents);
  const totalMarkup = n(summary?.total_markup_cents);
  const markedUpCount = n(summary?.marked_up_count);
  const totalCount = n(summary?.total_count);
  const markedUpSpend = campaignRows.reduce((sum, row) => sum + row.marked_up_spend_cents, 0);
  const blendedRate = marginRatePercent(totalMarkup, markedUpSpend);
  const topChannel = channelRows[0];
  const metricLoading = summaryQuery.isLoading || freshnessQuery.isLoading;

  // Spend columns carry gross now, roughly 1.4–1.8× their prior value. The
  // headers are renamed so a saved finance import fails loudly instead of
  // quietly reconciling against a different quantity.
  const exportRows = useMemo<Record<string, unknown>[]>(() => {
    if (tab === 'campaign') {
      return campaignRows.map((row) => ({
        'Campaign ID': row.campaign_id,
        Campaign: row.name,
        'Assigned production ID': row.production_id,
        Channel: row.channel,
        Status: row.status,
        'Agency margin enabled': row.marked_up,
        'Gross spend (cents)': row.spend_cents,
        'Marked-up gross spend (cents)': row.marked_up_spend_cents,
        'Agency margin (cents)': row.markup_cents,
      }));
    }
    const rows = tab === 'production' ? productionRows : channelRows;
    return rows.map((row) => ({
      ID: row.id,
      Name: row.name,
      'Campaigns marked up': row.marked_up_count,
      'Campaigns total': row.total_count,
      'Gross spend (cents)': row.spend_cents,
      'Marked-up gross spend (cents)': row.marked_up_spend_cents,
      'Agency margin (cents)': row.markup_cents,
      'Effective rate (%)': marginRatePercent(row.markup_cents, row.marked_up_spend_cents),
    }));
  }, [campaignRows, channelRows, productionRows, tab]);

  return (
    <div>
      <div className="page-hd">
        <div><h1 className="page-title">Markup</h1></div>
        <PageActions>
          <ReportingDataControls
            data-phone="meta"
            freshness={freshnessQuery.data?.freshness}
            dataset="delivery"
          />
          <DateFilter
            value={date}
            onChange={(next: { start: Date; end: Date }) => onSearchChange({
              from: fmtDateInput(next.start),
              to: fmtDateInput(next.end),
            })}
          />
          <button
            type="button"
            className="btn btn-icon-phone"
            data-phone="inline"
            aria-label="Export CSV"
            disabled={!canExport || exportRows.length === 0}
            onClick={() => downloadCsv(csvFilename(`markup-${tab}`), exportRows)}
            title={!canExport
              ? 'You do not have permission to export reports'
              : !deliveryAvailable
                ? `${deliveryReason} Export is disabled to avoid writing unavailable facts as zero.`
                : 'Export the active filtered view'}
          >
            <span>{Icons.dl}</span><span className="phone-long">Export</span>
          </button>
        </PageActions>
      </div>

      <div style={{ padding: '0 18px', borderBottom: '1px solid var(--border, var(--line))' }}>
        <TabStrip className="report-tabs" role="tablist" aria-label="Markup report views">
          {MARKUP_TABS.map((item) => (
            <Link
              to="/reports/markup"
              search={serializeOperationalReportSearch(
                updateMarkupReportSearch(search, { tab: item.id, scopeId: undefined }),
              )}
              preload="intent"
              role="tab"
              aria-selected={tab === item.id}
              key={item.id}
              className={`report-tab ${tab === item.id ? 'on' : ''}`}
              onKeyDown={(event) => {
                if (event.key !== ' ') return;
                event.preventDefault();
                event.currentTarget.click();
              }}
            >
              {item.label}
            </Link>
          ))}
        </TabStrip>
      </div>

      {tab === 'campaign' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
          <FilterLabel>Campaign</FilterLabel>
          <ScopeSearchPicker
            items={campaigns}
            value={scopeId}
            onChange={(next) => onSearchChange({ scopeId: next ?? undefined })}
            placeholder={campaignsQuery.isLoading ? 'Loading campaigns…' : 'Select campaign…'}
            searchPlaceholder="Search campaigns or productions…"
            getKey={(campaign) => campaign.id}
            getSearchText={(campaign) => `${campaign.name} ${productionMap.get(campaign.production_id)?.name ?? ''}`}
            renderRow={(campaign) => renderCampaignRow(campaign, productionMap)}
          />
        </ResponsiveFilters>
      )}
      {tab === 'production' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
          <FilterLabel>Assigned production</FilterLabel>
          <ScopeSearchPicker
            items={productions}
            value={scopeId}
            onChange={(next) => onSearchChange({ scopeId: next ?? undefined })}
            placeholder={productionsQuery.isLoading ? 'Loading productions…' : 'Select production…'}
            searchPlaceholder="Search productions…"
            getKey={(production) => production.id}
            getSearchText={(production) => production.name}
            renderRow={renderProductionRow}
          />
        </ResponsiveFilters>
      )}
      {tab === 'channel' && (
        <ResponsiveFilters className="filter-bar" style={{ justifyContent: 'flex-start', gap: 6 }}>
          <FilterLabel>Channel</FilterLabel>
          {CHANNELS.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`btn btn-sm ${channel === item.id ? 'btn-primary' : ''}`}
              onClick={() => onSearchChange({ channel: item.id })}
              style={{ minWidth: 90 }}
            >
              {item.name}
            </button>
          ))}
        </ResponsiveFilters>
      )}

      <ResponsiveKpis><div className="kpi-strip">
        <div className="kpi">
          <div className="kpi-label">Total Markup</div>
          <div className="kpi-val" title={!deliveryAvailable ? deliveryReason : undefined}>
            {metricLoading ? '…' : deliveryAvailable ? <MoneyCell value={totalMarkup / 100} dp={2}/> : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl">
              {deliveryAvailable ? `across ${markedUpCount} campaigns` : 'Delivery unavailable'}
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Markup On</div>
          <div className="kpi-val" title={!deliveryAvailable ? deliveryReason : undefined}>
            {metricLoading
              ? '…'
              : deliveryAvailable
                ? <>{markedUpCount}<span style={{ color: 'var(--ink-3)', fontSize: 14, fontWeight: 400 }}> / {totalCount}</span></>
                : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl">
              {deliveryAvailable
                ? `${totalCount ? (markedUpCount / totalCount * 100).toFixed(0) : '0'}% of campaigns`
                : 'No published delivery snapshot'}
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Blended Rate</div>
          <div className="kpi-val" title={!deliveryAvailable ? deliveryReason : undefined}>
            {metricLoading ? '…' : deliveryAvailable ? `${blendedRate.toFixed(1)}%` : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl">
              {deliveryAvailable
                ? `of ${fmt.$kpi(markedUpSpend / 100)} marked-up gross spend`
                : 'Delivery unavailable'}
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Markup / Gross Spend</div>
          <div className="kpi-val" title={!deliveryAvailable ? deliveryReason : undefined}>
            {metricLoading
              ? '…'
              : deliveryAvailable
                ? `${marginRatePercent(totalMarkup, totalSpend).toFixed(1)}%`
                : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl">{deliveryAvailable ? 'portfolio-wide' : 'Delivery unavailable'}</span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Top Channel</div>
          <div className="kpi-val" title={!deliveryAvailable ? deliveryReason : undefined}>
            {metricLoading ? '…' : deliveryAvailable ? topChannel?.name ?? '—' : '—'}
          </div>
          <div className="kpi-delta">
            <span className="lbl">
              {deliveryAvailable
                ? `${topChannel ? fmt.$(topChannel.markup_cents / 100) : '$0'} markup`
                : 'Delivery unavailable'}
            </span>
          </div>
        </div>
      </div></ResponsiveKpis>

      <ReportingFreshnessWarnings
        freshness={freshnessQuery.data?.freshness}
        dataset="delivery"
      />

      {(summaryQuery.isError || freshnessQuery.isError) && (
        <div style={{ padding: '12px 18px 0' }}>
          <QueryErrorNote
            error={summaryQuery.error ?? freshnessQuery.error}
            what="markup reporting"
            onRetry={() => {
              void summaryQuery.refetch();
              void freshnessQuery.refetch();
            }}
          />
        </div>
      )}

      {tab === 'overview' && (
        <div className="markup-overview-grid" style={{ padding: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="panel">
            <div className="panel-hd"><h3>Markup by channel</h3></div>
            <div className="panel-body">
              <QueryBlock
                query={byChannelQuery}
                rows={channelRows.length}
                what="markup by channel"
                unavailableReason={!deliveryAvailable ? deliveryReason : undefined}
              />
              {channelRows.map((row) => {
                const max = Math.max(...channelRows.map((item) => item.markup_cents), 1);
                const percent = row.markup_cents / max * 100;
                return (
                  <div key={row.id} className="markup-channel-summary" style={{ marginBottom: 14 }}>
                    <div className="flex between items-center" style={{ marginBottom: 4 }}>
                      <div className="flex items-center gap-2">
                        <ChannelPill channel={row.id as 'meta' | 'ctv' | 'dsp'}/>
                        <span className="muted" style={{ fontSize: 11 }}>{MARKUP_RATE_LABELS[row.id]}</span>
                      </div>
                      <span className="tnum" style={{ fontSize: 13 }}><b>{fmt.$full(row.markup_cents / 100)}</b></span>
                    </div>
                    <div className="gauge-track" style={{ height: 8, margin: '4px 0' }}>
                      <div className="gauge-fill" style={{ width: `${percent}%`, background: `var(--chart-${row.id === 'meta' ? 1 : row.id === 'ctv' ? 2 : 4})` }}/>
                    </div>
                    <div className="flex between" style={{ fontSize: 10.5, fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>
                      <span>{row.marked_up_count}/{row.total_count} campaigns on</span>
                      <span>{fmt.$(row.marked_up_spend_cents / 100)} marked-up spend</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel">
            <div className="panel-hd"><h3>Top assigned productions by markup</h3></div>
            <GroupedTable
              rows={productionRows.slice(0, 8)}
              query={byProductionQuery}
              nameHeader="Assigned production"
              unavailableReason={!deliveryAvailable ? deliveryReason : undefined}
            />
          </div>
        </div>
      )}

      {tab === 'campaign' && (
        <div style={{ padding: 18 }}>
          <div className="panel">
            <div className="panel-hd">
              <h3>{campaignMap.get(scopeId ?? '')?.name ?? 'All campaigns'}</h3>
              <div className="meta">
                {deliveryAvailable
                  ? `${campaignRows.length} ROW${campaignRows.length === 1 ? '' : 'S'}`
                  : 'DELIVERY UNAVAILABLE'}
              </div>
            </div>
            <CampaignTable
              rows={campaignRows}
              query={byCampaignQuery}
              productionMap={productionMap}
              unavailableReason={!deliveryAvailable ? deliveryReason : undefined}
            />
          </div>
        </div>
      )}

      {tab === 'channel' && (
        <div style={{ padding: 18 }}>
          <div className="panel">
            <div className="panel-hd">
              <h3><ChannelPill channel={channel}/></h3>
              <div className="meta">{MARKUP_RATE_LABELS[channel]}</div>
            </div>
            <GroupedTable
              rows={channelRows}
              query={byChannelQuery}
              nameHeader="Channel"
              channel
              unavailableReason={!deliveryAvailable ? deliveryReason : undefined}
            />
          </div>
        </div>
      )}

      {tab === 'production' && (
        <div style={{ padding: 18 }}>
          <div className="panel">
            <div className="panel-hd">
              <h3>{productionMap.get(scopeId ?? '')?.name ?? 'Markup by assigned production'}</h3>
              <div className="meta">
                {deliveryAvailable
                  ? `${productionRows.length} PRODUCTION${productionRows.length === 1 ? '' : 'S'}`
                  : 'DELIVERY UNAVAILABLE'}
              </div>
            </div>
            <GroupedTable
              rows={productionRows}
              query={byProductionQuery}
              nameHeader="Assigned production"
              unavailableReason={!deliveryAvailable ? deliveryReason : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function FilterLabel({ children }: { children: string }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-3)', marginRight: 6 }}>
      {children}
    </span>
  );
}

function QueryBlock({
  query,
  rows,
  what,
  unavailableReason,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => Promise<unknown> };
  rows: number;
  what: string;
  unavailableReason?: string;
}) {
  if (rows) return null;
  if (query.isError) {
    return <QueryErrorNote error={query.error} what={what} onRetry={() => void query.refetch()}/>;
  }
  return (
    <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 12 }}>
      {query.isLoading
        ? 'Loading…'
        : unavailableReason
          ? `${unavailableReason} Markup metrics are unavailable, not confirmed zero.`
          : 'No markup data in the selected window.'}
    </div>
  );
}

function TableState({
  query,
  rows,
  colSpan,
  what,
  unavailableReason,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => Promise<unknown> };
  rows: number;
  colSpan: number;
  what: string;
  unavailableReason?: string;
}) {
  if (rows) return null;
  return (
    <tr>
      <td colSpan={colSpan}>
        {query.isError
          ? <QueryErrorNote error={query.error} what={what} onRetry={() => void query.refetch()}/>
          : (
            <div className="muted" style={{ padding: 18, textAlign: 'center' }}>
              {query.isLoading
                ? 'Loading…'
                : unavailableReason
                  ? `${unavailableReason} Markup metrics are unavailable, not confirmed zero.`
                  : 'No markup data in the selected window.'}
            </div>
          )}
      </td>
    </tr>
  );
}

function CampaignTable({
  rows,
  query,
  productionMap,
  unavailableReason,
}: {
  rows: CampaignMarkup[];
  query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => Promise<unknown> };
  productionMap: ReadonlyMap<string, Production>;
  unavailableReason?: string;
}) {
  return (
    <ScrollTable className="markup-table-scroll" label="Campaign markup metrics" identityColumn={2}>
    <table className="tbl">
      <thead>
        <tr>
          <th>#</th><th>Campaign</th><th>Assigned production</th><th>Channel</th><th>Status</th>
          <th className="num">Gross spend</th><th style={{ textAlign: 'center' }}>Markup</th>
          <th>Rate</th><th className="num">Markup $</th>
        </tr>
      </thead>
      <tbody>
        <TableState
          query={query}
          rows={rows.length}
          colSpan={9}
          what="markup by campaign"
          unavailableReason={unavailableReason}
        />
        {rows.map((row, index) => (
          <tr key={row.campaign_id}>
            <td className="num" style={{ color: 'var(--ink-2)', width: 24 }}>{index + 1}</td>
            <td className="name">{row.name}</td>
            <td className="muted">{productionMap.get(row.production_id)?.name ?? row.production_id}</td>
            <td><ChannelPill channel={row.channel}/></td>
            <td><span className={`tag stat-${row.status}`}>{row.status}</span></td>
            <td className="num"><MoneyCell value={row.spend_cents / 100}/></td>
            <td style={{ textAlign: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, letterSpacing: '.06em', color: row.marked_up ? 'var(--good)' : 'var(--ink-3)' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: row.marked_up ? 'var(--good)' : 'var(--ink-3)', display: 'inline-block' }}/>
                {row.marked_up ? 'ON' : 'OFF'}
              </span>
            </td>
            <td className="mono" style={{ fontSize: 11.5, color: row.marked_up ? 'var(--ink-1)' : 'var(--ink-3)' }}>
              {row.marked_up ? MARKUP_RATE_LABELS[row.channel] ?? `${marginRatePercent(row.markup_cents, row.marked_up_spend_cents).toFixed(1)}%` : '—'}
            </td>
            <td className="num"><b style={{ color: row.marked_up ? 'var(--good)' : 'var(--ink-3)' }}>{row.marked_up ? fmt.$full(row.markup_cents / 100) : '—'}</b></td>
          </tr>
        ))}
      </tbody>
    </table>
    </ScrollTable>
  );
}

function GroupedTable({
  rows,
  query,
  nameHeader,
  channel = false,
  unavailableReason,
}: {
  rows: GroupedMarkup[];
  query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => Promise<unknown> };
  nameHeader: string;
  channel?: boolean;
  unavailableReason?: string;
}) {
  return (
    <ScrollTable className="markup-table-scroll" label={`${nameHeader} markup metrics`} identityColumn={1}>
    <table className="tbl">
      <thead>
        <tr>
          <th>{nameHeader}</th><th className="num">Campaigns On</th><th className="num">Gross Spend</th>
          <th className="num">Marked-up Gross</th><th className="num">Markup $</th><th className="num">% of Gross</th>
        </tr>
      </thead>
      <tbody>
        <TableState
          query={query}
          rows={rows.length}
          colSpan={6}
          what={`markup by ${nameHeader.toLowerCase()}`}
          unavailableReason={unavailableReason}
        />
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="name">{channel ? <ChannelPill channel={row.id as 'meta' | 'ctv' | 'dsp'}/> : row.name}</td>
            <td className="num">{row.marked_up_count}<span className="muted"> / {row.total_count}</span></td>
            <td className="num"><MoneyCell value={row.spend_cents / 100}/></td>
            <td className="num"><MoneyCell value={row.marked_up_spend_cents / 100}/></td>
            <td className="num"><b style={{ color: row.markup_cents > 0 ? 'var(--good)' : 'var(--ink-3)' }}>{row.markup_cents > 0 ? fmt.$full(row.markup_cents / 100) : '—'}</b></td>
            <td className="num"><span className="mono" style={{ color: 'var(--ink-2)' }}>{marginRatePercent(row.markup_cents, row.spend_cents).toFixed(1)}%</span></td>
          </tr>
        ))}
      </tbody>
    </table>
    </ScrollTable>
  );
}

export {
  MARKUP_RATE_LABELS,
  MarkupReports,
  aggregateCampaignRows,
  marginRatePercent,
};
